import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { EnvelopeSimple, EnvelopeOpen, ArrowUpRight, SpinnerGap } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function Inbox() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/me/inbox").then((r) => setData(r.data)).catch(() => setData({ messages: [], unread: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try { await api.post(`/me/inbox/${id}/read`); load(); } catch { /* */ }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="inbox-page">
      <p className="overline text-[#F27A54]">Photographer</p>
      <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-black tracking-tight">
        Inbox
        {data?.unread > 0 && <span className="rounded-full bg-[#F27A54] px-2 py-0.5 text-xs font-bold text-white" data-testid="inbox-unread">{data.unread} new</span>}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">Messages people send you through your photographer profile.</p>

      {!data ? (
        <div className="grid place-items-center py-20"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" weight="bold" /></div>
      ) : data.messages.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-border py-16 text-center" data-testid="inbox-empty">
          <EnvelopeSimple size={40} weight="thin" className="mx-auto text-zinc-300" />
          <p className="mt-3 font-display font-bold">No messages yet</p>
          <p className="mt-1 text-sm text-muted-foreground">When someone contacts you from your profile, it appears here.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {data.messages.map((m) => (
            <div key={m.id} data-testid={`inbox-msg-${m.id}`}
              className={`rounded-2xl border p-4 transition-colors ${m.read ? "border-border bg-white" : "border-[#F27A54]/40 bg-[#FFF6F2]"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-bold text-zinc-800">
                    {m.read ? <EnvelopeOpen size={16} className="text-zinc-400" /> : <EnvelopeSimple size={16} weight="fill" className="text-[#F27A54]" />}
                    {m.from_name}
                    {m.from_email && <a href={`mailto:${m.from_email}`} className="text-xs font-semibold text-[#F27A54] hover:underline">{m.from_email}</a>}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">to <Link to={`/photographer/${encodeURIComponent(m.key)}`} className="font-semibold text-zinc-600 hover:text-[#F27A54]">{m.key} <ArrowUpRight size={10} className="inline" /></Link> · {timeAgo(m.created_at)}</p>
                </div>
                {!m.read && (
                  <button onClick={() => markRead(m.id)} data-testid={`inbox-read-${m.id}`}
                    className="shrink-0 rounded-full border border-border px-3 py-1 text-xs font-bold text-zinc-600 hover:border-[#F27A54] hover:text-[#F27A54]">Mark read</button>
                )}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-zinc-700">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
